import { useEffect } from 'react';
import { useQuery } from 'react-query';
import leavePolicyApi, { LeaveType } from '../network/leavePolicy';
import {
  DEFAULT_LEAVE_TYPES,
  registerLeaveTypeLabels,
} from '../utils/timeaway';

export const leaveTypesQueryKey = (includeInactive: boolean) =>
  ['leave-types', includeInactive] as const;

/**
 * The company's leave types from the API. While they load (or if loading
 * fails) this returns the built-in defaults, so forms always have options.
 * Pass `includeInactive` for filters and history, which must still show
 * deactivated types.
 */
export function useLeaveTypes(options: { includeInactive?: boolean } = {}) {
  const includeInactive = Boolean(options.includeInactive);
  const query = useQuery(
    leaveTypesQueryKey(includeInactive),
    () => leavePolicyApi.getLeaveTypes(includeInactive),
    { staleTime: 5 * 60 * 1000, retry: false },
  );

  useEffect(() => {
    if (query.data?.length) registerLeaveTypeLabels(query.data);
  }, [query.data]);

  const leaveTypes: LeaveType[] = query.data?.length
    ? query.data
    : DEFAULT_LEAVE_TYPES;

  return {
    leaveTypes,
    isLoading: query.isLoading,
    isError: query.isError,
    isFallback: !query.data?.length,
    refetch: query.refetch,
  };
}
